/**
 * Polling that is kind to the Worker: one poller per thing watched (a second start for the
 * same key replaces the first), a backoff from 1.5 s towards 8 s, nothing asked while the tab
 * is hidden, and never more than two requests in the air from one tab. Six eager pollers from
 * one browser once tripped Cloudflare's resource limit.
 */
export const POLL_MIN_MS = 1500;
export const POLL_MAX_MS = 8000;
export const POLL_GROWTH = 1.6;
export const POLL_MAX_IN_FLIGHT = 2;

export type PollHandle = { readonly key: string; readonly cancelled: boolean; cancel(): void };

export type PollOptions<T> = {
  request: () => Promise<T>;
  isDone: (result: T) => boolean;
  /** Every answer, the final one included. */
  onResult: (result: T) => void;
  /** A failed request; return true to keep polling. */
  onError: (error: unknown, failures: number) => boolean;
  /** Delay before the first request; the backoff starts after it. */
  first?: number;
};

const active = new Map<string, Handle>();
let inFlight = 0;

class Handle implements PollHandle {
  cancelled = false;
  timer: number | null = null;
  onShow: () => void = () => {};
  constructor(readonly key: string) {}
  cancel() {
    this.cancelled = true;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    document.removeEventListener('visibilitychange', this.onShow);
    if (active.get(this.key) === this) active.delete(this.key);
  }
}

export function activePolls(): PollHandle[] {
  return [...active.values()];
}

export function startPoll<T>(key: string, opts: PollOptions<T>): PollHandle {
  active.get(key)?.cancel();
  const handle = new Handle(key);
  active.set(key, handle);
  let delay = POLL_MIN_MS;
  let failures = 0;

  const schedule = (ms: number) => {
    if (handle.cancelled) return;
    handle.timer = window.setTimeout(tick, ms);
  };
  const later = () => {
    schedule(delay);
    delay = Math.min(POLL_MAX_MS, Math.round(delay * POLL_GROWTH));
  };
  const tick = () => {
    handle.timer = null;
    if (handle.cancelled) return;
    if (document.hidden) {
      // Nothing while the tab is away; the next look happens the moment it is back.
      handle.onShow = () => {
        document.removeEventListener('visibilitychange', handle.onShow);
        if (!document.hidden) tick();
      };
      document.addEventListener('visibilitychange', handle.onShow);
      return;
    }
    if (inFlight >= POLL_MAX_IN_FLIGHT) {
      // The air is full: wait a beat and try for a slot again, no request made.
      schedule(POLL_MIN_MS);
      return;
    }
    inFlight += 1;
    opts.request().then(
      (result) => {
        inFlight -= 1;
        if (handle.cancelled) return;
        failures = 0;
        opts.onResult(result);
        if (opts.isDone(result)) handle.cancel();
        else later();
      },
      (error: unknown) => {
        inFlight -= 1;
        if (handle.cancelled) return;
        failures += 1;
        if (opts.onError(error, failures)) later();
        else handle.cancel();
      },
    );
  };
  schedule(opts.first ?? 0);
  return handle;
}
